import { Component, Input, Output, EventEmitter } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { BlockItemComponent } from '../block-item/block-item.component';
import { ClinicContentBlock } from '../../../data/models/ClinicContentBlock';
import { ClinicContentBlockType } from '../../../data/models/ClinicContentBlockType';
import { Clinic } from '../../../data/models/Clinic';
import { ChangeTrackerService } from '../../../helper/services/change-tracker.service';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { TextBlockDialogComponent } from '../../dialogs/text-block-dialog/text-block-dialog.component';
import { ImageBlockDialogComponent } from '../../dialogs/image-block-dialog/image-block-dialog.component';
import { VideoBlockDialogComponent } from '../../dialogs/video-block-dialog/video-block-dialog.component';
import { ButtonBlockDialogComponent } from '../../dialogs/button-block-dialog/button-block-dialog.component';
import { ContactPersonDialogComponent } from '../../dialogs/contact-person-dialog/contact-person-dialog.component';
import { EditClinicDialogComponent } from '../../dialogs/edit-clinic-dialog/edit-clinic-dialog.component';
import { AddBlockTypeDialogComponent } from '../../dialogs/add-block-type-dialog/add-block-type-dialog.component';

@Component({
  selector: 'app-block-editor',
  imports: [BlockItemComponent, MatButtonModule, MatIconModule],
  templateUrl: './block-editor.component.html',
  styleUrl: './block-editor.component.scss'
})
export class BlockEditorComponent {
  @Input() blocks: ClinicContentBlock[] = []
  @Input() blockTypes: ClinicContentBlockType[] = []
  @Input() clinic!: Clinic
  @Output() blocksChange = new EventEmitter<ClinicContentBlock[]>()
  @Output() clinicChange = new EventEmitter<Clinic>()

  private localLogoUrl: string | null = null

  constructor(
    private dialog: MatDialog,
    private changeTracker: ChangeTrackerService
  ) {}

  getBlockNumber(block: ClinicContentBlock): number {
    const sameType = this.blocks.filter(b => b.block_type.name === block.block_type.name)
    return sameType.findIndex(b => b.key === block.key) + 1
  }

  getLocalLogoUrl(block: ClinicContentBlock): string | null {
    if (block.block_type.name === 'logo') return this.localLogoUrl
    return null
  }

  moveBlockUp(index: number): void {
    if (index <= 0) return
    const newBlocks = [...this.blocks]
    const movedBlock = newBlocks[index]
    const targetBlock = newBlocks[index - 1]
    const targetOrder = targetBlock.order
    movedBlock.order = targetOrder
    targetBlock.order = targetOrder + 1
    newBlocks.sort((a, b) => a.order - b.order)
    this.blocks = newBlocks
    this.blocksChange.emit(this.blocks)
    this.changeTracker.addChange({ operation: 'REORDER_BLOCK', blockKey: movedBlock.key, payload: { order: movedBlock.order } })
  }

  moveBlockDown(index: number): void {
    if (index >= this.blocks.length - 1) return
    const newBlocks = [...this.blocks]
    const movedBlock = newBlocks[index]
    const targetBlock = newBlocks[index + 1]
    const targetOrder = targetBlock.order
    movedBlock.order = targetOrder
    targetBlock.order = targetOrder - 1
    newBlocks.sort((a, b) => a.order - b.order)
    this.blocks = newBlocks
    this.blocksChange.emit(this.blocks)
    this.changeTracker.addChange({ operation: 'REORDER_BLOCK', blockKey: movedBlock.key, payload: { order: movedBlock.order } })
  }

  togglePublish(block: ClinicContentBlock): void {
    block.is_published = !block.is_published
    this.blocksChange.emit([...this.blocks])
    this.changeTracker.addChange({ operation: 'TOGGLE_PUBLISH_BLOCK', blockKey: block.key })
  }

  toggleCollapsible(block: ClinicContentBlock): void {
    block.is_collapsible = !block.is_collapsible
    this.blocksChange.emit([...this.blocks])
    this.changeTracker.addChange({ operation: 'TOGGLE_COLLAPSIBLE_BLOCK', blockKey: block.key })
  }

  editBlock(block: ClinicContentBlock): void {
    if (block.block_type.is_standard) {
      this.editClinicField(block)
      return
    }

    let dialogComponent: any
    switch (block.block_type.name) {
      case 'text': dialogComponent = TextBlockDialogComponent; break
      case 'image': dialogComponent = ImageBlockDialogComponent; break
      case 'video': dialogComponent = VideoBlockDialogComponent; break
      case 'button': dialogComponent = ButtonBlockDialogComponent; break
      case 'contact_person': dialogComponent = ContactPersonDialogComponent; break
    }
    if (!dialogComponent) return

    const dialogRef = this.dialog.open(dialogComponent, { data: { block }, width: '500px', disableClose: true })
    dialogRef.afterClosed().subscribe(result => {
      if (!result || result === 'back') return
      Object.assign(block, result)
      this.setLocalMediaPreview(block, result)
      this.blocksChange.emit([...this.blocks])
      this.changeTracker.addChange({
        operation: 'UPDATE_BLOCK',
        blockKey: block.key,
        payload: result,
        file: result.file,
        previewFile: result.previewFile
      })
    })
  }

  editClinicField(block: ClinicContentBlock): void {
    const dialogRef = this.dialog.open(EditClinicDialogComponent, {
      data: { blockTypeName: block.block_type.name, clinic: this.clinic },
      width: '500px'
    })
    dialogRef.afterClosed().subscribe(result => {
      if (!result) return
      if (result.type === 'clinic_update') {
        Object.assign(this.clinic, result.payload)
        this.updateStandardBlocks()
        this.clinicChange.emit({ ...this.clinic })
        this.changeTracker.addChange({ operation: 'UPDATE_CLINIC', payload: result.payload })
      } else if (result.type === 'logo_upload') {
        this.localLogoUrl = URL.createObjectURL(result.file)
        const logoBlock = this.blocks.find(b => b.block_type.name === 'logo')
        if (logoBlock) {
          logoBlock.logo_path = this.localLogoUrl
        }
        this.blocksChange.emit([...this.blocks])
        this.clinicChange.emit({ ...this.clinic })
        this.changeTracker.addChange({ operation: 'UPLOAD_LOGO', file: result.file })
      }
    })
  }

  deleteBlock(block: ClinicContentBlock): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Block löschen', message: 'Möchtest du diesen Block wirklich löschen?', confirmText: 'Löschen' }
    })
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return
      this.blocks = this.blocks.filter(b => b.key !== block.key)
      this.blocksChange.emit(this.blocks)
      this.changeTracker.addChange({ operation: 'DELETE_BLOCK', blockKey: block.key })
    })
  }

  addBlock(): void {
    const typeDialogRef = this.dialog.open(AddBlockTypeDialogComponent, {
      data: { blockTypes: this.blockTypes },
      width: '400px'
    })
    typeDialogRef.beforeClosed().subscribe(selectedType => {
      if (!selectedType) return
      this.openCreateDialog(selectedType)
    })
  }

  private openCreateDialog(blockType: ClinicContentBlockType): void {
    let dialogComponent: any
    switch (blockType.name) {
      case 'text': dialogComponent = TextBlockDialogComponent; break
      case 'image': dialogComponent = ImageBlockDialogComponent; break
      case 'video': dialogComponent = VideoBlockDialogComponent; break
      case 'button': dialogComponent = ButtonBlockDialogComponent; break
      case 'contact_person': dialogComponent = ContactPersonDialogComponent; break
    }
    if (!dialogComponent) return

    const dialogRef = this.dialog.open(dialogComponent, { data: {}, width: '500px', disableClose: true })
    dialogRef.beforeClosed().subscribe(result => {
      if (result === 'back') {
        this.addBlock()
      }
    })
    dialogRef.afterClosed().subscribe(result => {
      if (!result || result === 'back') return
      const tempKey = 'temp-' + crypto.randomUUID()
      const newBlock = new ClinicContentBlock()
      newBlock.key = tempKey
      newBlock.block_type = blockType
      newBlock.order = this.blocks.length > 0 ? Math.max(...this.blocks.map(b => b.order)) + 1 : 0
      newBlock.is_published = false
      Object.assign(newBlock, result)
      this.setLocalMediaPreview(newBlock, result)
      this.blocks.push(newBlock)
      this.blocksChange.emit([...this.blocks])
      this.changeTracker.addChange({
        operation: 'CREATE_BLOCK',
        tempKey: tempKey,
        payload: { block_type_key: blockType.key, ...result, is_published: false },
        file: result.file,
        previewFile: result.previewFile
      })
    })
  }

  private setLocalMediaPreview(block: ClinicContentBlock, result: any): void {
    if (result.file && result.file instanceof File) {
      const blobUrl = URL.createObjectURL(result.file)
      switch (block.block_type.name) {
        case 'image':
          block.image_path = blobUrl
          break
        case 'video':
          block.video_path = blobUrl
          break
      }
    }
    if (result.previewFile && result.previewFile instanceof File) {
      block.video_preview_path = URL.createObjectURL(result.previewFile)
    }
  }

  private updateStandardBlocks(): void {
    for (const block of this.blocks) {
      if (!block.block_type.is_standard) continue
      switch (block.block_type.name) {
        case 'address':
          block.street = this.clinic.street
          block.postal_code = this.clinic.postal_code
          block.city = this.clinic.city
          break
        case 'phone': block.phone = this.clinic.phone; break
        case 'website': block.website = this.clinic.website; break
        case 'logo': block.logo_path = this.clinic.logo_path; break
      }
    }
  }
}
